<template>
  <section class="shared-workspace" id="workspacePanel" v-show="workspaceOpen || !currentSessionKey">
    <ChatSearchPanel v-if="chatSearchOpen" class="chat-search-panel-overlay" />
    <!-- 无会话时的缺省状态 -->
    <template v-if="!currentSessionKey">
      <div class="workspace-no-session">
        <div class="workspace-no-session-icon">
          <svg viewBox="0 0 48 48" fill="none" width="48" height="48">
            <rect x="6" y="10" width="36" height="28" rx="6" stroke="#c8d4e3" stroke-width="2.5" fill="none"/>
            <path d="M14 20h20M14 27h12" stroke="#c8d4e3" stroke-width="2.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="workspace-no-session-title">选择会话以开启工作区</div>
        <div class="workspace-no-session-sub">从左侧选择一个 Agent 或群组会话，工作区内容将在此展示</div>
      </div>
    </template>
    <template v-else>
    <div class="workspace-head">
      <div>
        <h2 class="workspace-title">{{ currentAgentProfile ? (currentAgentProfile.workspaceTitle || currentAgentProfile.name + '的电脑') : '工作区' }}</h2>
        <div class="workspace-sub">展示当前工作过程与搜索内容</div>
      </div>
      <div class="workspace-actions">
        <button class="workspace-btn primary" id="takeOverWorkspaceBtn" @click="toggleTakeover">
          {{ workspaceTakeover ? '退出接管' : '接管电脑' }}
        </button>
        <button class="workspace-btn" id="workspaceFullscreenBtn" @click="toggleFullscreen">
          {{ isWide && !workspaceTakeover ? '退出全屏' : '全屏查看' }}
        </button>
      </div>
      <button class="workspace-more-btn" id="workspaceMoreBtn" type="button" aria-label="更多操作">⋯</button>
    </div>

    <div class="workspace-body">
      <div class="workspace-browser-dock" id="workspaceBrowserDock">
        <WorkspaceBrowserShell />
      </div>

      <div class="workspace-detail-stack">
        <article
          class="workspace-worklog"
          id="workspaceResultPanel"
          :class="workspaceResultPanelClass"
        >
          <!-- Desktop mode -->
          <div class="workspace-desktop-stack">
            <div class="workspace-desktop-copy">
              <div class="workspace-desktop-title">桌面内容</div>
              <div class="workspace-desktop-sub">当前状态：桌面待命</div>
            </div>
            <div class="workspace-desktop-item">
              <div class="workspace-desktop-item-title">Google Chrome</div>
              <div class="workspace-desktop-item-desc">准备发起搜索任务，等待用户输入问题。</div>
            </div>
            <div class="workspace-desktop-item">
              <div class="workspace-desktop-item-title">项目文件</div>
              <div class="workspace-desktop-item-desc">当前项目原型与会话草稿已在桌面待命。</div>
            </div>
            <div class="workspace-desktop-item">
              <div class="workspace-desktop-item-title">下载目录</div>
              <div class="workspace-desktop-item-desc">等待新的网页资料、图片或附件进入工作流。</div>
            </div>
          </div>

          <!-- Processing mode -->
          <div class="workspace-process-stack">
            <section class="workspace-process-group">
              <div class="workspace-process-head">
                <div class="workspace-process-title" id="workspaceProcessSection1Title">{{ content.searchTitle || '搜索和收集主流自主 Agent 信息' }}</div>
                <span class="workspace-process-caret">⌃</span>
              </div>
              <p class="workspace-process-desc" id="workspaceProcessSection1Desc">{{ content.searchDesc || '已确认调研目前主流自主 Agent，准备收集不同类别的详细信息。' }}</p>
              <div class="workspace-process-chip-list">
                <div class="workspace-process-chip"><span class="workspace-process-chip-icon">⌕</span><span id="workspaceProcessChip1">{{ content.searchChip1 }}</span></div>
                <div class="workspace-process-chip"><span class="workspace-process-chip-icon">⌕</span><span id="workspaceProcessChip2">{{ content.searchChip2 }}</span></div>
                <div class="workspace-process-chip"><span class="workspace-process-chip-icon">⌕</span><span id="workspaceProcessChip3">{{ content.searchChip3 }}</span></div>
              </div>
            </section>
            <section class="workspace-process-group">
              <div class="workspace-process-head">
                <div class="workspace-process-title" id="workspaceProcessSection2Title">整理并向用户呈现结果</div>
                <span class="workspace-process-caret">⌃</span>
              </div>
              <p class="workspace-process-desc" id="workspaceProcessSection2Desc">{{ content.resultDesc }}</p>
              <div class="workspace-process-chip-list">
                <div class="workspace-process-chip"><span class="workspace-process-chip-icon">✎</span><span id="workspaceProcessChip4">{{ content.resultChip2 }}</span></div>
              </div>
            </section>
          </div>

          <!-- Result mode -->
          <div class="workspace-result-stack">
            <div class="workspace-inline-file" id="workspaceResultFileName">{{ resultData.fileName }}</div>
            <h3 class="workspace-report-doc-title" id="workspaceResultTitle" v-html="resultTitleHtml"></h3>
            <section class="workspace-report-section">
              <h4 class="workspace-report-heading">引言</h4>
              <p class="workspace-report-paragraph" id="workspaceResultSummary">{{ resultData.summary }}</p>
            </section>
            <section class="workspace-report-section">
              <h4 class="workspace-report-heading" id="workspaceResultCoreHeading">{{ resultData.coreHeading }}</h4>
              <ul class="workspace-report-list" id="workspaceResultHighlights">
                <li><strong>自主性（Autonomy）</strong>：能够独立地制定计划并执行任务。</li>
                <li><strong>感知（Perception）</strong>：能够从环境中获取信息并理解其含义。</li>
                <li><strong>决策（Decision-making）</strong>：能够根据感知到的信息和预设目标做出合理选择。</li>
                <li><strong>行动（Action）</strong>：能够通过执行操作来改变环境或实现目标。</li>
                <li><strong>学习（Learning）</strong>：能够从经验中学习，优化其行为和决策过程。</li>
              </ul>
            </section>
            <section class="workspace-report-section">
              <h4 class="workspace-report-heading" id="workspaceResultSectionTitle">{{ resultData.sectionTitle }}</h4>
              <p class="workspace-report-paragraph" id="workspaceResultSectionDesc">{{ resultData.sectionDesc }}</p>
              <div class="workspace-report-tags" id="workspaceResultDeliverables">
                <span class="workspace-report-tag">{{ resultData.fileName }}</span>
                <span class="workspace-report-tag">框架分类</span>
                <span class="workspace-report-tag">能力对比</span>
                <span class="workspace-report-tag">适用场景</span>
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>

    <!-- Group workspace panel (for group sessions) -->
    <GroupWorkspacePanel />

    </template>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import WorkspaceBrowserShell from './WorkspaceBrowserShell.vue'
import GroupWorkspacePanel from './GroupWorkspacePanel.vue'
import ChatSearchPanel from './ChatSearchPanel.vue'

export default {
  name: 'SharedWorkspace',
  components: { WorkspaceBrowserShell, GroupWorkspacePanel, ChatSearchPanel },
  computed: {
    ...mapState(['workspaceOpen', 'workspaceScene', 'workspaceTakeover', 'workspaceFloat', 'processingContent', 'workspaceResultData', 'currentSessionKey', 'chatSearchOpen']),
    ...mapGetters(['currentAgentProfile']),
    content() { return this.processingContent },
    resultData() { return this.workspaceResultData },
    isWide() { return this.workspaceFloat },
    workspaceResultPanelClass() {
      return {
        'mode-desktop': this.workspaceScene === 'desktop',
        'mode-process': this.workspaceScene === 'processing',
        'mode-result': this.workspaceScene === 'result',
        'is-ready': this.workspaceScene === 'result'
      }
    },
    resultTitleHtml() {
      const title = this.resultData.title || ''
      const isAutonomous = /Autonomous Agents/i.test(title)
      if (isAutonomous) return '自主Agent（<span>Autonomous Agents</span>）调研报告'
      return title
    }
  },
  methods: {
    ...mapActions(['openWorkspaceFullscreen', 'openWorkspaceTakeover', 'closeWorkspaceFullscreen']),
    toggleFullscreen() {
      if (this.isWide && !this.workspaceTakeover) {
        this.closeWorkspaceFullscreen()
      } else {
        this.openWorkspaceFullscreen()
      }
    },
    toggleTakeover() {
      if (this.workspaceTakeover) {
        this.closeWorkspaceFullscreen()
      } else {
        this.openWorkspaceTakeover()
      }
    }
  }
}
</script>
